// @ts-strict-ignore
import React, {
  useContext,
  useEffect, useMemo, useState,
} from 'react';
import { datadogRum } from '@datadog/browser-rum';
import {
  Badge,
  Banner,
  Box,
  ButtonSize,
  Icon,
  IconName,
  Modal,
} from '@usealloy/component-library';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import type { Action, ThresholdResults } from '~/dashboard/views/workflows/store/engine/types';
import {
  selectLegacyMappings,
  selectShouldRenderCriticalNodePath,
  selectWorkflowData,
} from '~/dashboard/views/workflows/store/redux/selectors';
import {
  StyledHeader,
  StyledSectionHeader,
  StyledTagDescription,
  StyledToggleButton,
} from './styles';
import TagRule from './TagRule';
import { useThresholdResults } from './hooks';
import { reversedObjectKeyVal } from './utils';
import EvidencesTable from './EvidencesTable';
import { UserContext } from '~/dashboard/context';
import type { Dimension } from '~/application-review/redux/types';
import { EditorContainerType } from 'flow/store/redux/constants';
import {
  resetNodeAndConnectorState,
  setDimensions,
  setEditorContainerType,
  setEntityToken,
  setEvaluationTagNames,
  setEvaluationToken,
  setFocusedNodeId,
  setThresholdOutputResults,
  setEvaluationOutcome,
  setIsRuleExplainabilityPortfolioEvaluation,
  setSelectedEvidenceThresholdId,
  setMatrixModelResults,
} from 'flow/store/redux/slice';
import { selectSelectedEvidenceThresholdId } from 'flow/store/redux/selectors/workflowFeatures';
import { toggleShouldRenderCriticalNodePath } from 'flow/store/redux/thunks';
import { EditorAccessProvider } from 'flow/editor/context';
import { useQuery } from '@tanstack/react-query';
import apiCalls from '~/helpers/apiCalls';
import { useInitializeWorkflowRedux } from 'flow/editor/hooks/useInitializeWorkflowRedux';

type TagRuleModalProps = {
  actionName?: string;
  outcome?: string | null;
  tagDescription?: string;
  closeModal: () => void;
  entityToken: string;
  evaluationTags: Array<{ tag_name: string }>;
  evaluationToken: string;
  isPortfolioEvaluation: boolean;
  dimensions?: Dimension[];
  workflowToken: string | null;
  workflowVersion: number | null;
};

// Defaulting dimensions to a new array inline causes an infinite update loop in some cases.
// Defaulting dimensions to a pre-defined initialDimensions fixes this issue.
const initialDimensions = [];

const TagRuleModal = ({
  actionName,
  outcome = null,
  tagDescription,
  closeModal,
  entityToken,
  evaluationTags,
  evaluationToken,
  isPortfolioEvaluation,
  dimensions = initialDimensions,
  workflowToken,
  workflowVersion,
}: TagRuleModalProps) => {
  const {
    permissions,
    featureFlags,
  } = useContext(UserContext);

  const canViewRules = !!permissions['READ:ApplicationVersions:null'];

  const dispatch = useAppDispatch();
  const workflowData = useAppSelector(selectWorkflowData);

  const shouldInitializeWorkflow = useMemo(() => Boolean(
    workflowToken
    && workflowVersion
    && (
      !workflowData?.token
      || !workflowData?.publicVersion
      || workflowData?.token !== workflowToken
      || workflowData?.publicVersion !== workflowVersion
    ),
  ), [workflowData?.token, workflowData?.publicVersion, workflowToken, workflowVersion]);

  useInitializeWorkflowRedux({
    workflowToken,
    versionId: workflowVersion,
    featureFlags,
    enabled: shouldInitializeWorkflow,
    reinitialize: true,
  });

  const legacyMappings = useAppSelector(selectLegacyMappings);
  const thresholdId = useAppSelector(selectSelectedEvidenceThresholdId);
  const shouldRenderCriticalNodePath = useAppSelector(selectShouldRenderCriticalNodePath);
  const [actionToShow, setActionToShow] = useState<Action>(null);
  const [thresholdResults, setThresholdResults] = useState<ThresholdResults>(null);
  const [
    isRuleExplainabilityDataLoaded,
    setIsRuleExplainabilityDataLoaded,
  ] = useState<boolean>(false);

  useQuery({
    queryKey: ['getEvaluation', entityToken, evaluationToken],
    queryFn: async () => {
      const response = await apiCalls.entities.getEvaluation({ entityToken, evaluationToken });
      const matrixModelResults = response?.event?.final_response?.summary?.matrix_models;
      dispatch(setMatrixModelResults(matrixModelResults));
      return matrixModelResults;
    },
  });

  useEffect(() => {
    dispatch(setEvaluationTagNames((evaluationTags || []).map(({ tag_name }) => tag_name)));
    if (outcome) dispatch(setEvaluationOutcome(outcome));
  }, [dispatch, evaluationTags, outcome]);

  const workflowLoading = useMemo(() => !workflowData?.actions, [workflowData]);

  /**
   * initWorkflowPropsAndData (called by useInitializeWorkflowRedux) resets workflowFeatures,
   * which will overwrite existing tokens, so we wait until the workflow finishes loading
   * before setting tokens.
   */
  useEffect(() => {
    if (!workflowLoading) {
      dispatch(setEntityToken(entityToken));
      dispatch(setEvaluationToken(evaluationToken));
      dispatch(setDimensions(dimensions));
    }
  }, [workflowLoading, dimensions, dispatch, entityToken, evaluationToken]);

  const {
    data: thresholdResultsData,
    isLoading: thresholdResultsLoading,
    isError: thresholdResultsError,
  } = useThresholdResults({
    entityToken,
    evaluationToken,
    canViewRules,
    isPortfolioEvaluation,
    dimensions,
  });

  const reversedActionMappings = reversedObjectKeyVal(legacyMappings?.actions || {});

  const legacyActionId = useMemo(() => (
    reversedActionMappings?.[actionToShow?.publicId] || null
  ), [actionToShow, reversedActionMappings]);

  const targetAction = useMemo(() => {
    const actionValues = Object.values(workflowData?.actions ?? {});
    if (actionName) {
      return actionValues.find(actionValue => (
        actionValue?.tag?.name === actionName
        || actionValue?.matrixModel?.name === actionName
        || actionValue?.input?.name === actionName
      ));
    }
    return null;
  }, [actionName, workflowData?.actions]);

  useEffect(() => {
    if (targetAction && !workflowLoading) {
      dispatch(setFocusedNodeId(targetAction.publicId));
      dispatch(setEditorContainerType(EditorContainerType.MODAL));
      setActionToShow(targetAction);
    }
  }, [workflowLoading, dispatch, targetAction]);

  useEffect(() => {
    // Always process threshold results when available, even if from cache
    // This ensures the effect runs on remount with cached data
    if (actionToShow && legacyMappings && thresholdResultsData) {
      // eslint-disable-next-line no-underscore-dangle
      const resultsData = thresholdResultsData?._embedded;
      const thresholdResultsByAction = resultsData?.actions?.[legacyActionId];

      if (thresholdResultsByAction) {
        setThresholdResults({
          ...thresholdResultsByAction,
          is_portfolio_evaluation: isPortfolioEvaluation,
        });
        dispatch(setThresholdOutputResults(resultsData?.actions));
        dispatch(setIsRuleExplainabilityPortfolioEvaluation(Boolean(isPortfolioEvaluation)));
      }
      setIsRuleExplainabilityDataLoaded(true);
    }

    if (!actionName) {
      setIsRuleExplainabilityDataLoaded(true);
    }
  }, [
    actionName,
    actionToShow,
    thresholdResultsData,
    legacyMappings,
    legacyActionId,
    isPortfolioEvaluation,
    dispatch,
  ]);

  const criticalPathViewToggleButton = (
    <StyledToggleButton
      data-pendo-anchor="evaluation-explainability-critical-path-toggle"
      icon={shouldRenderCriticalNodePath ? IconName.View : IconName.EyeSlash}
      onClick={() => dispatch(toggleShouldRenderCriticalNodePath())}
      size={ButtonSize.xsmall}
    >
      {
        shouldRenderCriticalNodePath
          ? 'Show Unset Tags'
          : 'Hide Unset Tags'
      }
    </StyledToggleButton>
  );

  const Header = (
    <StyledHeader justifyContent="flex-start" alignItems="center" gap={2}>
      {featureFlags?.ruleExplainabilityPhase >= 4
        ? (
          <>
            <Icon icon={IconName.Workflows} data-pendo-anchor="evaluation-explainability-read-only-workflow" />
            {workflowData && !shouldInitializeWorkflow && (
              <>
                {workflowData.name}
                <Badge outline style={{ borderColor: 'var(--cl-gray-600)' }}>{`v${workflowData.publicVersion}`}</Badge>
              </>
            )}
            {featureFlags?.ruleExplainabilityPhase >= 5 && criticalPathViewToggleButton}
          </>
        )
        : actionName}
    </StyledHeader>
  );

  useEffect(() => {
    if (actionName && tagDescription) datadogRum.addAction('explainability-hasTagDescription', { actionName });
  }, [actionName, tagDescription]);
  useEffect(() => {
    if (actionName && thresholdResultsError) datadogRum.addAction('explainability-hasThresholdResultsError', { actionName });
  }, [actionName, thresholdResultsError]);

  return (
    <Modal
      isOpen
      header={Header}
      // Modal has no footer so this is modal spacing from viewport (64px) + modal header height (72px)
      maxHeight={136}
      onClose={() => {
        dispatch(resetNodeAndConnectorState());
        dispatch(setEditorContainerType(EditorContainerType.PAGE));
        dispatch(setSelectedEvidenceThresholdId(null));
        closeModal();
      }}
      style={{
        overflowX: 'hidden',
      }}
      size={featureFlags?.ruleExplainabilityPhase >= 4 ? 'xl' : 'medium'}
    >
      <EditorAccessProvider>
        <Box
          direction="column"
          alignItems="flex-start"
          gap={featureFlags?.ruleExplainabilityPhase >= 4 ? 0 : 5}
          style={{
            height: featureFlags?.ruleExplainabilityPhase >= 4 ? '100%' : 'auto',
          }}
        >
          {
            !!tagDescription && featureFlags?.ruleExplainabilityPhase < 4 && (
              <Box direction="column" alignItems="flex-start" gap={3}>
                <StyledSectionHeader size={1}>
                  Description
                </StyledSectionHeader>
                <StyledTagDescription>
                  {tagDescription}
                </StyledTagDescription>
              </Box>
            )
          }
          <Banner
            open={!!thresholdResultsError}
            outline
            borderColor="var(--cl-yellow-300)"
            variant="info"
          >
            Outputs for this rule are unavailable or do not exist
          </Banner>
          <Banner
            open={!canViewRules}
            outline
            borderColor="var(--cl-red-300)"
            variant="error"
          >
            {'You don\'t have permission to view this rule'}
          </Banner>
          <TagRule
            action={actionToShow}
            thresholdResults={thresholdResults}
            loading={(
              shouldInitializeWorkflow
              || thresholdResultsLoading
              || workflowLoading
              || !isRuleExplainabilityDataLoaded
            )}
            canViewRules={canViewRules}
          />
          <div style={{ width: '100%' }}>
            {thresholdId !== null && featureFlags?.ruleExplainabilityPhase < 4 && (
              <EvidencesTable
                thresholdId={thresholdId}
                actionId={legacyActionId}
                entityToken={entityToken}
                evaluationToken={evaluationToken}
                isPortfolioEvaluation={isPortfolioEvaluation}
                dimensions={dimensions}
              />
            )}
          </div>
        </Box>
      </EditorAccessProvider>
    </Modal>
  );
};

export default TagRuleModal;
